import { useState, useEffect } from "react";
import { Loader2, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import api from "../../lib/api";
import { SUPPLIER_CATEGORIES } from "./categories";

const EMPTY = {
  name: "", contact_name: "", email: "", phone: "", address: "", notes: "", category_hint: "otros",
};

/**
 * Unified create/edit supplier dialog.
 * - When `supplier` is null → create mode (calls createSupplier)
 * - When `supplier` is provided → edit mode (calls updateSupplier + optional delete)
 * Editing works even for AI auto-suggested suppliers.
 */
export default function SupplierDialog({ open, onOpenChange, supplier, restaurantId, onSaved, onDeleted }) {
  const isEdit = !!supplier;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (supplier) {
      setForm({
        name: supplier.name || "",
        contact_name: supplier.contact_name || "",
        email: supplier.email || "",
        phone: supplier.phone || "",
        address: supplier.address || "",
        notes: supplier.notes || "",
        category_hint: supplier.category_hint || "otros",
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, supplier]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => {
        if (payload[k] === "") payload[k] = null;
      });
      let saved;
      if (isEdit) {
        saved = await api.updateSupplier(supplier.supplier_id, payload);
        toast.success("Proveedor actualizado");
      } else {
        saved = await api.createSupplier(restaurantId, payload);
        toast.success("Proveedor creado");
      }
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "Error al guardar proveedor");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!supplier) return;
    setDeleting(true);
    try {
      await api.deleteSupplier(supplier.supplier_id);
      toast.success("Proveedor eliminado");
      onDeleted?.(supplier.supplier_id);
      setConfirmDelete(false);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "Error al eliminar");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isEdit ? "Editar proveedor" : "Nuevo proveedor"}
              {isEdit && supplier?.auto_suggested && (
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-gradient-to-r from-violet-100 to-fuchsia-100 text-violet-700 border border-violet-200">
                  <Sparkles className="h-3 w-3" /> Sugerido por IA
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              {isEdit
                ? "Modifica los datos. Los proveedores sugeridos por IA también se pueden editar libremente."
                : "Añade un proveedor manualmente."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre empresa *</Label>
              <Input
                placeholder="Nombre del proveedor"
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                data-testid="supplier-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoría</Label>
                <Select
                  value={form.category_hint}
                  onValueChange={(v) => setForm(f => ({ ...f, category_hint: v }))}
                >
                  <SelectTrigger data-testid="supplier-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPLIER_CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Persona de contacto</Label>
                <Input
                  placeholder="Nombre del contacto"
                  value={form.contact_name}
                  onChange={(e) => setForm(f => ({ ...f, contact_name: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="email@proveedor.com"
                  value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  placeholder="+34 600 000 000"
                  value={form.phone}
                  onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Dirección</Label>
              <Input
                placeholder="Dirección o zona"
                value={form.address}
                onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                placeholder="Condiciones, horarios, etc."
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              {isEdit ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-red-700 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setConfirmDelete(true)}
                  data-testid="supplier-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1.5" />
                  Eliminar
                </Button>
              ) : <span />}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-supplier-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? "Guardar" : "Crear")}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar proveedor?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <strong>{supplier?.name}</strong> permanentemente. Los pedidos y recuentos históricos no se borrarán.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
              onClick={handleDelete}
              data-testid="supplier-delete-confirm"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
